/**
 * 调整图层的参考实现与 LUT 构建（M2-15 / DESIGN.md §5.4）。
 *
 * §5.4：「曲线/色阶/HSL → 1D LUT 或 3D LUT 纹理采样，GPU」。
 * 本模块是**数值权威**：曲线/色阶烘成 256 项 LUT，HSL 给出逐像素公式；
 * GPU 着色器（src/gpu/shaders/adjust）按同一公式实现，playwright 对拍
 * 容差 1/255。CPU 合成桥也直接用这里的函数——显示与导出因此与 GPU 一致。
 */

export interface CurvePoint {
  /** 输入 0–255。 */
  readonly x: number;
  /** 输出 0–255。 */
  readonly y: number;
}

/**
 * 曲线 → LUT。单调三次 Hermite（Fritsch–Carlson）：过所有控制点、
 * 一阶连续、且**不产生过冲**——普通 Catmull-Rom 在陡峭段会冲出 [0,255]，
 * 表现为高光/阴影翻转，调色工具不能有这种惊喜。
 */
export function curvesLut(points: readonly CurvePoint[]): Uint8Array {
  const sorted = [...points].sort((a, b) => a.x - b.x);
  if (sorted.length < 2) {
    // 不足两点视为恒等
    const identity = new Uint8Array(256);
    for (let i = 0; i < 256; i++) identity[i] = i;
    return identity;
  }
  const xs = sorted.map((p) => p.x);
  const ys = sorted.map((p) => p.y);
  const n = xs.length;

  // 区间斜率
  const h: number[] = [];
  const delta: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    h.push(Math.max(1e-6, xs[i + 1]! - xs[i]!));
    delta.push((ys[i + 1]! - ys[i]!) / h[i]!);
  }
  // Fritsch–Carlson 切线
  const m: number[] = [delta[0]!];
  for (let i = 1; i < n - 1; i++) {
    if (delta[i - 1]! * delta[i]! <= 0) m.push(0);
    else {
      const w1 = 2 * h[i]! + h[i - 1]!;
      const w2 = h[i]! + 2 * h[i - 1]!;
      m.push((w1 + w2) / (w1 / delta[i - 1]! + w2 / delta[i]!));
    }
  }
  m.push(delta[n - 2]!);

  const lut = new Uint8Array(256);
  for (let v = 0; v < 256; v++) {
    let out: number;
    if (v <= xs[0]!) out = ys[0]!;
    else if (v >= xs[n - 1]!) out = ys[n - 1]!;
    else {
      let i = 0;
      while (xs[i + 1]! < v) i++;
      const t = (v - xs[i]!) / h[i]!;
      const t2 = t * t;
      const t3 = t2 * t;
      out =
        ys[i]! * (2 * t3 - 3 * t2 + 1) +
        h[i]! * m[i]! * (t3 - 2 * t2 + t) +
        ys[i + 1]! * (-2 * t3 + 3 * t2) +
        h[i]! * m[i + 1]! * (t3 - t2);
    }
    lut[v] = Math.round(Math.min(255, Math.max(0, out)));
  }
  return lut;
}

export interface LevelsParams {
  /** 输入黑/白场 0–255。 */
  readonly inBlack: number;
  readonly inWhite: number;
  /** 伽马 0.1–10（1 = 不变）。 */
  readonly gamma: number;
  /** 输出黑/白场 0–255。 */
  readonly outBlack: number;
  readonly outWhite: number;
}

/** 色阶 → LUT（Photoshop 的映射公式）。 */
export function levelsLut(params: LevelsParams): Uint8Array {
  const { inBlack, inWhite, gamma, outBlack, outWhite } = params;
  const lut = new Uint8Array(256);
  const range = Math.max(1e-6, inWhite - inBlack);
  for (let v = 0; v < 256; v++) {
    const normalized = Math.min(1, Math.max(0, (v - inBlack) / range));
    const graded = normalized ** (1 / gamma);
    lut[v] = Math.round(Math.min(255, Math.max(0, outBlack + graded * (outWhite - outBlack))));
  }
  return lut;
}

/** LUT 应用到一个像素通道值。 */
export function applyLutValue(lut: Uint8Array, value: number): number {
  return lut[value]!;
}

export interface HslParams {
  /** 色相偏移，度（−180–180）。 */
  readonly hue: number;
  /** 饱和度 −100–100（−100 全灰）。 */
  readonly saturation: number;
  /** 明度 −100–100（Photoshop 语义：向白/黑混合）。 */
  readonly lightness: number;
}

/**
 * HSL 调整（单像素，sRGB 字节进出）。
 * GPU 着色器逐行对照这份公式实现，改一边必须同步另一边。
 */
export function adjustHsl(
  r: number,
  g: number,
  b: number,
  params: HslParams,
): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d > 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
    else if (max === gn) h = ((bn - rn) / d + 2) / 6;
    else h = ((rn - gn) / d + 4) / 6;
  }

  h = (h + params.hue / 360 + 1) % 1;
  s = Math.min(1, Math.max(0, s * (1 + params.saturation / 100)));
  let l2 = l;
  if (params.lightness > 0) l2 = l + (1 - l) * (params.lightness / 100);
  else if (params.lightness < 0) l2 = l * (1 + params.lightness / 100);

  const q = l2 < 0.5 ? l2 * (1 + s) : l2 + s - l2 * s;
  const p = 2 * l2 - q;
  const channel = (t: number): number => {
    const tt = (t + 1) % 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return [
    Math.round(Math.min(255, Math.max(0, channel(h + 1 / 3) * 255))),
    Math.round(Math.min(255, Math.max(0, channel(h) * 255))),
    Math.round(Math.min(255, Math.max(0, channel(h - 1 / 3) * 255))),
  ];
}

/** 恒等 LUT。 */
export function identityLut(): Uint8Array {
  const lut = new Uint8Array(256);
  for (let i = 0; i < 256; i++) lut[i] = i;
  return lut;
}
